import { Entity } from '@backstage/catalog-model';
import { Link, TableColumn } from '@backstage/core-components';
import { CatalogTable, CatalogTableRow } from '@backstage/plugin-catalog';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { Box, Chip, Typography } from '@material-ui/core';
import { entityProcesses, tierColumn } from '../ServiceTier';

type Column = TableColumn<CatalogTableRow>;

const repoOf = (entity: Entity) =>
  entity.metadata.annotations?.['github.com/project-slug'];

/** The risk flags to show for an entity, in display order. */
const handlesOf = (entity: Entity) => {
  const { payments, pii } = entityProcesses(entity);
  return [payments && 'Payments', pii && 'PII'].filter(Boolean) as string[];
};

const nameColumn = CatalogTable.columns.createNameColumn({
  defaultKind: 'Component',
});

const serviceColumn: Column = {
  ...nameColumn,
  title: 'Service',
  render: ({ entity }) => (
    <Box>
      <EntityRefLink entityRef={entity} defaultKind="Component" hideIcon />
      <Typography
        variant="caption"
        color="textSecondary"
        display="block"
        noWrap
        style={{ maxWidth: 300 }}
      >
        {entity.metadata.description}
      </Typography>
    </Box>
  ),
};

const teamColumn: Column = {
  ...CatalogTable.columns.createOwnerColumn(),
  title: 'Team',
};

const repoColumn: Column = {
  title: 'Repo',
  customSort: (a, b) =>
    (repoOf(a.entity) ?? '').localeCompare(repoOf(b.entity) ?? ''),
  customFilterAndSearch: (term, { entity }) =>
    (repoOf(entity) ?? '').toLowerCase().includes(String(term).toLowerCase()),
  render: ({ entity }) => {
    const slug = repoOf(entity);
    return slug ? (
      <Link to={`https://github.com/${slug}`}>{slug.split('/')[1]}</Link>
    ) : (
      '-'
    );
  },
};

const handlesColumn: Column = {
  title: 'Handles',
  // Flagged services first when sorting.
  customSort: (a, b) => handlesOf(b.entity).length - handlesOf(a.entity).length,
  customFilterAndSearch: (term, { entity }) =>
    handlesOf(entity)
      .join(' ')
      .toLowerCase()
      .includes(String(term).toLowerCase()),
  render: ({ entity }) => {
    const flags = handlesOf(entity);
    if (!flags.length) return '-';
    return (
      <Box display="flex" style={{ gap: 4 }}>
        {flags.map(flag => (
          <Chip key={flag} size="small" variant="outlined" label={flag} />
        ))}
      </Box>
    );
  },
};

/** Keeps short values on one line so only the service column wraps. */
const noWrap = (column: Column): Column => ({
  ...column,
  cellStyle: { whiteSpace: 'nowrap' },
});

/** Service, team, tier and repo up front, then lifecycle and whether it handles payments or PII. */
export const serviceColumns: Column[] = [
  serviceColumn,
  ...[
    teamColumn,
    tierColumn,
    repoColumn,
    CatalogTable.columns.createSpecLifecycleColumn(),
    handlesColumn,
  ].map(noWrap),
];
